/* eslint-disable no-continue */

const groupTokensIntoPhrases = (tokens: string[], phraseSize = 3): string[] => {
  return Array.from({ length: Math.ceil(tokens.length / phraseSize) }, (_, i) =>
    tokens.slice(i * phraseSize, (i + 1) * phraseSize).join('')
  );
};

/**
 * Extracts the next word (with trailing whitespace) from text starting at startIndex.
 * Returns the word token and the index after the word.
 */
const extractWord = (text: string, startIndex: number): { word: string; endIndex: number } => {
  let currentIndex = startIndex;

  // Capture non-whitespace characters
  while (currentIndex < text.length && text[currentIndex] !== ' ' && text[currentIndex] !== '\n') {
    currentIndex += 1;
  }

  // Capture trailing whitespace (spaces and newlines)
  while (
    currentIndex < text.length &&
    (text[currentIndex] === ' ' || text[currentIndex] === '\n')
  ) {
    currentIndex += 1;
  }

  return {
    word: text.slice(startIndex, currentIndex),
    endIndex: currentIndex
  };
};

const handleDelimitedMarkdown = (
  text: string,
  startIndex: number,
  openingDelimiter: string
): { isValidMarkdown: boolean; endIndex: number; token: string } => {
  const delimiterLength = openingDelimiter.length;

  // Check if there's content immediately after opening delimiter (no space)
  if (startIndex + delimiterLength >= text.length || text[startIndex + delimiterLength] === ' ') {
    return { isValidMarkdown: false, endIndex: startIndex + 1, token: '' };
  }

  let currentIndex = startIndex + delimiterLength; // Skip past the opening delimiter

  // Find the closing delimiter
  while (currentIndex <= text.length - openingDelimiter.length) {
    if (text.slice(currentIndex, currentIndex + openingDelimiter.length) === openingDelimiter) {
      // Check that there's no space before the closing delimiter
      if (text[currentIndex - 1] !== ' ') {
        currentIndex += openingDelimiter.length; // Include the closing delimiter
        return {
          isValidMarkdown: true,
          endIndex: currentIndex,
          token: text.slice(startIndex, currentIndex)
        };
      }
      break;
    }
    currentIndex += 1;
  }

  return { isValidMarkdown: false, endIndex: startIndex + 1, token: '' };
};

const handleLineBasedMarkdown = (
  text: string,
  startIndex: number,
  openingChar: string,
  maxCount: number = 1
): { isValidMarkdown: boolean; endIndex: number; token: string } => {
  // Check if this character is at the beginning of a line
  const isAtLineStart = startIndex === 0 || text[startIndex - 1] === '\n';

  if (!isAtLineStart) {
    return { isValidMarkdown: false, endIndex: startIndex + 1, token: '' };
  }

  let currentIndex = startIndex;
  let charCount = 0;

  // Count consecutive opening characters
  while (currentIndex < text.length && text[currentIndex] === openingChar && charCount < maxCount) {
    charCount += 1;
    currentIndex += 1;
  }

  // Check if there's a space after the opening characters
  if (currentIndex < text.length && text[currentIndex] === ' ') {
    // Find the end of the line (or end of text)
    while (currentIndex < text.length && text[currentIndex] !== '\n') {
      currentIndex += 1;
    }

    // Include the newline character if present
    if (currentIndex < text.length && text[currentIndex] === '\n') {
      currentIndex += 1;
    }

    return {
      isValidMarkdown: true,
      endIndex: currentIndex,
      token: text.slice(startIndex, currentIndex)
    };
  }

  return { isValidMarkdown: false, endIndex: startIndex + 1, token: '' };
};

const markdownTokenizer = (
  text: string,
  { tokenizeMarkdown = false, phraseSize = 3 }: { tokenizeMarkdown?: boolean; phraseSize?: number }
): string[] => {
  if (!text) return [];

  if (!tokenizeMarkdown) {
    const wordRegex = /\S+\s*/g;
    const words = text.match(wordRegex) || [];
    return groupTokensIntoPhrases(words, phraseSize);
  }

  const tokens: string[] = [];
  let currentTokenIndex = 0;

  while (currentTokenIndex < text.length) {
    const char = text[currentTokenIndex];

    // Handle code blocks (```text```)
    if (
      char === '`' &&
      text[currentTokenIndex + 1] === '`' &&
      text[currentTokenIndex + 2] === '`'
    ) {
      // Add the opening ``` as one token
      tokens.push('```');
      currentTokenIndex += 3; // Skip past the opening ```

      // Check if there's a language identifier on the same line
      let languageEndIndex = currentTokenIndex;
      while (languageEndIndex < text.length && text[languageEndIndex] !== '\n') {
        languageEndIndex += 1;
      }

      // If there's a language identifier, add it as one token including the newline
      if (languageEndIndex > currentTokenIndex) {
        const languageLine = text.slice(
          currentTokenIndex,
          languageEndIndex + (text[languageEndIndex] === '\n' ? 1 : 0)
        );
        tokens.push(languageLine);
        currentTokenIndex = languageEndIndex + (text[languageEndIndex] === '\n' ? 1 : 0);
      }

      // Now stream the content character by character until we find closing ```
      while (currentTokenIndex < text.length - 2) {
        if (
          text[currentTokenIndex] === '`' &&
          text[currentTokenIndex + 1] === '`' &&
          text[currentTokenIndex + 2] === '`'
        ) {
          // Add the closing ``` as one token
          tokens.push('```');
          currentTokenIndex += 3; // Include the closing ```
          break;
        }

        // Extract word from code block
        const { word, endIndex } = extractWord(text, currentTokenIndex);
        tokens.push(word);
        currentTokenIndex = endIndex;
      }

      continue;
    }

    // Markdown characters that surround the text
    if (['*', '`', '~'].includes(char)) {
      let openingDelimiter = char;

      // Bold case
      if (text[currentTokenIndex + 1] === char) {
        openingDelimiter = `${char}${char}`;
      }

      const result = handleDelimitedMarkdown(text, currentTokenIndex, openingDelimiter);

      if (result.isValidMarkdown) {
        tokens.push(result.token);
        currentTokenIndex = result.endIndex;
        continue;
      }

      // Not valid markdown, extract as word
      const { word, endIndex } = extractWord(text, currentTokenIndex);
      tokens.push(word);
      currentTokenIndex = endIndex;

      continue;
    }

    // Markdown characters that start on a new line and add a space between actual text and the markdown character
    if (['#', '>'].includes(char)) {
      const result = handleLineBasedMarkdown(text, currentTokenIndex, char, char === '#' ? 6 : 1);
      if (result.isValidMarkdown) {
        tokens.push(result.token);
        currentTokenIndex = result.endIndex;
        continue;
      }

      // Not valid markdown, extract as word
      const { word, endIndex } = extractWord(text, currentTokenIndex);
      tokens.push(word);
      currentTokenIndex = endIndex;

      continue;
    }

    // Image and link markdown elements
    if (char === '[' || (char === '!' && text[currentTokenIndex + 1] === '[')) {
      const startIndex = currentTokenIndex;
      currentTokenIndex += char === '[' ? 1 : 2; // Skip past the opening

      // Find the closing ] and then check for (
      while (currentTokenIndex < text.length) {
        if (text[currentTokenIndex] === ']' && text[currentTokenIndex + 1] === '(') {
          currentTokenIndex += 2; // Skip past ](

          // Find the closing )
          while (currentTokenIndex < text.length) {
            if (text[currentTokenIndex] === ')') {
              currentTokenIndex += 1; // Include the closing )
              break;
            }
            currentTokenIndex += 1;
          }
          break;
        }
        currentTokenIndex += 1;
      }

      tokens.push(text.slice(startIndex, currentTokenIndex));
      continue;
    }

    // Default: extract word
    const { word, endIndex } = extractWord(text, currentTokenIndex);
    tokens.push(word);
    currentTokenIndex = endIndex;
  }

  return groupTokensIntoPhrases(tokens, phraseSize);
};

export default markdownTokenizer;
